import React, { useState, useCallback, useEffect, useRef } from "react";
import { getLoggedinUser } from "../../userStore";
import ClinicalSummaryLogsModal from "./logsModal";
import { useForm, useWatch } from "react-hook-form";
import { DatePickerField } from "../../components/ReactHooksFormFields/DatePickerField";
import moment from "moment";
import { clinicalSummaryApi } from "../../redux/service/clinicalSummary_service";
import { toast } from "react-toastify";
import ShareClinicalSummaryModal from "./shareClinicalSummaryModal";
import axios from "axios";
import { AiOutlineClose } from "react-icons/ai";
import FullScreenLoader from "../../components/FullScreenLoader";
import { getBaseUrl } from "../../utils/common_lib";

function ClinicalSummary() {
  const user = getLoggedinUser();
  const authToken = user?.token;
  const practice_id = user?.practice_id;
  const pid = user?.pid;
  const { control, setValue } = useForm({});

  const watchStartDate = useWatch({
    control,
    name: "start_date",
  });

  const watchEndDate = useWatch({
    control,
    name: "end_date",
  });

  const [showClinicalSummaryLogsModal, setShowClinicalSummaryLogsModal] =
    useState(false);
  const [ccdFileUrl, setCcdFileUrl] = useState("");
  const [openShareSummaryModal, setOpenShareSummaryModal] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;

    const handleIframeLoad = () => {
      setIframeLoaded(true);
    };

    if (iframe) {
      iframe.addEventListener("load", handleIframeLoad);
    }

    return () => {
      if (iframe) {
        iframe.removeEventListener("load", handleIframeLoad);
      }
    };
  }, [iframeRef]);

  const [fetchCcdFileUrlTrigger] =
    clinicalSummaryApi.endpoints.getCcdFileUrl.useLazyQuery();

  function returnClinicalSummaryUrl() {
    let url = `${getBaseUrl()}/PatientClinicals/view.json?token=${authToken}&practice_id=${practice_id}&pid=${pid}&tz=-240`;
    if (!watchStartDate || !watchEndDate) {
      return url;
    } else {
      return `${url}&from=${moment(watchStartDate).format(
        "MM/DD/YYYY"
      )}&to=${moment(watchEndDate).format("MM/DD/YYYY")}`;
    }
  }

  const handleFetchCcdFileUrl = useCallback(() => {
    setLoading(true);
    fetchCcdFileUrlTrigger({
      type: "transmit",
    })
      .then((resp) => {
        setCcdFileUrl(resp?.data?.data?.file_name || "");
      })
      .catch((err) => {
        console.log("Error fetching CCD file url");
        toast.error("Error fetching CCD file url");
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleShowClinicalSummaryLogsModal = useCallback(() => {
    setShowClinicalSummaryLogsModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    if (
      (!watchStartDate && !watchEndDate) ||
      (watchStartDate && watchEndDate)
    ) {
      setCcdFileUrl("");
      setLoading(true);
      setIframeUrl(returnClinicalSummaryUrl());
      handleFetchCcdFileUrl();
    } else {
      toast.error("Please select both start and end dates");
    }
  };

  const downloadFile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${getBaseUrl()}/PatientClinicals/download_ccd.json?tz=-240&portal=patient&practice_id=${practice_id}&is_patient=1&token=${authToken}&pid=${pid}`,
        {
          responseType: "blob",
        }
      );

      // Create a new blob with the response data and correct MIME type for zip
      const blob = new Blob([response.data], { type: "application/zip" });

      // Create a link element
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${user?.lname || ""}_${user?.fname}${moment().format("YYYYMMDD")}.zip`
      ); // Set the file name with .zip extension

      // Append the link to the body, trigger a click, and remove the link
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading the file", error);
      toast.error("Error downloading the file");
    }
    setLoading(false);
  };

  // useEffect(() => {
  //   handleFetchCcdFileUrl();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  console.log("re-renders in clinical summary page");

  return (
    <>
      {(loading || (ccdFileUrl && !isIframeLoaded)) && <FullScreenLoader />}
      <div className="overflow-y-auto">
        {showClinicalSummaryLogsModal && (
          <ClinicalSummaryLogsModal
            setShowClinicalSummaryLogsModal={setShowClinicalSummaryLogsModal}
          />
        )}

        {openShareSummaryModal && (
          <ShareClinicalSummaryModal
            setOpenShareSummaryModal={setOpenShareSummaryModal}
            pid={pid}
            ccdFileUrl={ccdFileUrl}
          />
        )}
        <div className="flex border-gray-300 bg-gray-200 px-3 py-3 sm:px-4 flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="flex gap-5 items-center">
            <h3 className="text-base font-semibold leading-6 text-gray-900">
              Clinical Summary
            </h3>
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              onClick={handleShowClinicalSummaryLogsModal}
              className="relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Logs
            </button>
          </div>
        </div>
        <div className="flex w-[100%] gap-[25px] px-4 items-end">
          <div className="relative">
            <DatePickerField
              control={control}
              name="start_date"
              title="From Date"
              dateTime={false}
              maxDate={watchEndDate}
            />
            {watchStartDate && (
              <div
                className="absolute cursor-pointer right-[6px] bottom-[8px] z-[999] text-lg"
                onClick={(e) => {
                  setValue("start_date", "");
                }}
              >
                <AiOutlineClose />
              </div>
            )}
          </div>
          <div className="relative">
            <DatePickerField
              control={control}
              name="end_date"
              title="To Date"
              dateTime={false}
              minDate={watchStartDate}
            />
            {watchEndDate && (
              <div
                className="absolute cursor-pointer right-[6px] bottom-[8px] z-[999] text-lg"
                onClick={(e) => {
                  setValue("end_date", "");
                }}
              >
                <AiOutlineClose />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleSearchClick}
            className="relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 h-[35px]"
          >
            Search
          </button>
        </div>
        <div className="h-[100vh] mt-[20px] mb-[40px]" id="iframe-div">
          {(!loading || !isIframeLoaded) && ccdFileUrl && (
            <iframe
              ref={!ccdFileUrl || loading ? null : iframeRef}
              name={!ccdFileUrl || loading ? null : "clinical_summary_iframe"}
              id={!ccdFileUrl || loading ? null : "clinical_summary_iframe"}
              title="Clinical Summary Logs"
              height="100%"
              width="100%"
              src={!ccdFileUrl || loading ? null : iframeUrl}
              onLoad={() => {
                setIframeLoaded(true);
                setLoading(false);
              }}
            />
          )}
        </div>
      </div>
      {ccdFileUrl && (
        <div className="w-[79.6%] flex justify-end gap-[10px] fixed bottom-[49px] p-[17px]">
          <button
            type="button"
            className="relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={downloadFile}
          >
            Download
          </button>
          <button
            type="button"
            className="relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => setOpenShareSummaryModal(true)}
          >
            Transmit
          </button>
        </div>
      )}
    </>
  );
}

export default React.memo(ClinicalSummary);
